// src/pages/form/FormListPage.tsx
// Form list — the home of the form module. Search, filter by publish state,
// row actions (publish/close/archive/duplicate/delete). `+ New` opens the
// desktop-app create dialog (FormCreateDialog, built on the WIP form-kit).
// Replaces the generated CRUD scaffold — see docs/ux/form-ia.md.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useForm, useI18n } from '@react/hooks';
import { Alert, Badge, Button } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { Form, FormStatus } from '@core/types/form';
import { FormCreateDialog } from '@/features/form/FormCreateDialog';
import { FORM_STATUSES, errMessage, formStatusBadgeVariant } from '@/features/form/helpers';

export const FormListPage = observer(() => {
  const { t } = useI18n();
  const store = useForm();

  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | FormStatus>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState<Form | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = () => {
    setError(null);
    store.ListForms().catch((e: unknown) => setError(errMessage(e, t('form.list.loadError'))));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = store.items.filter((f) => {
    if (statusFilter && f.status !== statusFilter) return false;
    if (search.trim() && !f.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  });

  const withBusy = async (id: string, action: () => Promise<unknown>) => {
    setBusyId(id);
    setError(null);
    try {
      await action();
      load();
    } catch (e) {
      setError(errMessage(e, t('form.list.actionError')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('form.list.title')}</h1>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/forms/templates" className="text-sm text-primary hover:underline">
            {t('form.list.templatesLink')}
          </Link>
          <Link to="/forms/policies" className="text-sm text-primary hover:underline">
            {t('form.list.policiesLink')}
          </Link>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            {t('form.list.newForm')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('form.list.searchPlaceholder')}
          className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground"
        />
        <div className="w-48">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FormStatus | '')}
            options={[
              { value: '', label: t('form.list.allStatuses') },
              ...FORM_STATUSES.map((s) => ({ value: s, label: t(`form.status.${s.replace('FORM_STATUS_', '').toLowerCase()}`) })),
            ]}
            aria-label={t('form.list.filterStatus')}
          />
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {store.isLoading && store.items.length === 0 && (
        <div className="space-y-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      )}

      {!store.isLoading && filtered.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-10 text-center">
          <p className="text-sm text-muted-foreground">{t('form.list.empty')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('form.list.subtitle')}</p>
          <Button size="sm" className="mt-3" onClick={() => setCreateOpen(true)}>
            {t('form.list.newForm')}
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((f) => (
          <div key={f.entity?.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
            <Link to={`/forms/${f.entity?.id}`} className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-foreground">{f.name}</span>
                <Badge variant={formStatusBadgeVariant(f.status)}>
                  {t(`form.status.${(f.status ?? 'FORM_STATUS_DRAFT').replace('FORM_STATUS_', '').toLowerCase()}`)}
                </Badge>
              </div>
              {f.description && <p className="mt-0.5 truncate text-sm text-muted-foreground">{f.description}</p>}
            </Link>
            <div className="flex shrink-0 flex-wrap gap-1.5">
              {f.status === 'FORM_STATUS_DRAFT' && (
                <Button
                  size="sm"
                  variant="secondary"
                  isLoading={busyId === f.entity?.id}
                  onClick={() => withBusy(f.entity?.id ?? '', () => store.PublishForm(f.entity!.id, { form: f.entity! }))}
                >
                  {t('form.list.publish')}
                </Button>
              )}
              {f.status === 'FORM_STATUS_PUBLISHED' && (
                <Button
                  size="sm"
                  variant="secondary"
                  isLoading={busyId === f.entity?.id}
                  onClick={() => withBusy(f.entity?.id ?? '', () => store.CloseForm(f.entity!.id, { form: f.entity! }))}
                >
                  {t('form.list.close')}
                </Button>
              )}
              {f.status !== 'FORM_STATUS_ARCHIVED' && (
                <Button
                  size="sm"
                  variant="secondary"
                  isLoading={busyId === f.entity?.id}
                  onClick={() => withBusy(f.entity?.id ?? '', () => store.ArchiveForm(f.entity!.id, { form: f.entity! }))}
                >
                  {t('form.list.archive')}
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                isLoading={busyId === f.entity?.id}
                onClick={() => withBusy(f.entity?.id ?? '', () => store.DuplicateForm(f.entity!.id, {}))}
              >
                {t('form.list.duplicate')}
              </Button>
              <Button size="sm" variant="danger" onClick={() => setDeleting(f)}>
                {t('common.delete')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <FormCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onError={setError} />

      <ConfirmDialog
        open={!!deleting}
        title={t('form.list.deleteTitle')}
        description={t('form.list.deleteDescription')}
        destructive
        isConfirming={store.isDeleting}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting?.entity?.id) return;
          try {
            await store.DeleteForm(deleting.entity.id);
            setDeleting(null);
          } catch (e) {
            setError(errMessage(e, t('form.list.deleteError')));
            setDeleting(null);
          }
        }}
      />
    </div>
  );
});
